import { RefObject, useLayoutEffect, useState } from "react";

type EdgePosition = {
  showAbove: boolean;
  showToLeft: boolean;
  borderRadius: string;
  isReady: boolean;
};

const R = "var(--border-radius-minimal)";

export function computeBorderRadius(showAbove: boolean, showToLeft: boolean, radius = R): string {
  if (!showAbove && !showToLeft) return `0 ${radius} ${radius} ${radius}`;
  if (showAbove && !showToLeft) return `${radius} ${radius} ${radius} 0`;
  if (!showAbove && showToLeft) return `${radius} 0 ${radius} ${radius}`;
  return `${radius} ${radius} 0 ${radius}`;
}

export function useEdgeAwarePosition(
  anchorRef: RefObject<HTMLElement>,
  tooltipRef: RefObject<HTMLElement>,
  isVisible: boolean,
  preferAbove = false,
): EdgePosition {
  const [state, setState] = useState<EdgePosition>({
    showAbove: false,
    showToLeft: false,
    borderRadius: computeBorderRadius(false, false),
    isReady: false,
  });

  useLayoutEffect(() => {
    if (!isVisible) {
      setState((prev) => ({ ...prev, isReady: false }));
      return;
    }
    if (!anchorRef.current || !tooltipRef.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const tooltipWidth = tooltipRef.current.offsetWidth;
    const tooltipHeight = tooltipRef.current.offsetHeight;

    const showAbove = preferAbove
      ? anchorRect.top >= tooltipHeight
      : window.innerHeight - anchorRect.bottom - tooltipHeight < 0;
    const showToLeft = window.innerWidth - anchorRect.left - tooltipWidth < 0;

    setState({
      showAbove,
      showToLeft,
      borderRadius: computeBorderRadius(showAbove, showToLeft),
      isReady: true,
    });
  }, [isVisible, preferAbove, anchorRef, tooltipRef]);

  return state;
}
